import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
/**
 * `GET /api/me/crew` — the sidebar's My Crew widget.
 *
 * Mirrors `CrewSummary` on the backend. `birthYear` is stored rather than
 * age because the age is wrong within twelve months of being typed — the
 * client derives it against today at render time. `photoUrl` is a
 * short-lived signed URL minted per read, so nothing needs invalidating.
 * `coParent` is a plain name (no invitation flow yet) and is `null` when
 * the "Your crew" step was skipped.
 */
/**
 * What this child was last doing — the crew card's sub-line.
 *
 * ── Parts, not a sentence ────────────────────────────────────────────────
 * `moduleOrdinal` / `moduleTotal` rather than a `moduleLabel` string, because
 * "Module 2" is English and this is a French-first product: a label minted on
 * the server could not be translated by the client that draws it. The client
 * composes it (`crew.lastModule`), so both locales read naturally.
 *
 * Both are **null on a content type with no modules** — that is the signal to
 * draw the title alone rather than a course row with a missing count.
 *
 * `moduleOrdinal` is a ONE-BASED PLACE, not a stored position. Positions in
 * this API are fractional and spaced at multiples of 65 536; rendering one as
 * an ordinal gives "Module 65537".
 */
export const CrewLastActivity = z.object({
    contentItemId: z.string().uuid(),
    contentType: z.string(),
    path: z.string(),
    title: z.string(),
    moduleOrdinal: z.number().int().positive().nullable(),
    moduleTotal: z.number().int().positive().nullable(),
    at: z.string().datetime(),
});
export const CrewChild = z.object({
    id: z.string().uuid(),
    name: z.string(),
    birthYear: z.number().int().nullable(),
    /**
     * Free text, max 60, `null` when not stated — and free text on purpose.
     *
     * This codebase reaches for an enum wherever a closed set is drawn as a
     * picker (`RoomIcon` is seven members for that reason) and a pronoun set is
     * not one: **iel** is ordinary in Québec French, and an enum would push a
     * real answer into an *other* bucket on the one screen where getting a
     * child's pronoun wrong is the kind of wrong a parent notices.
     *
     * Null renders as nothing. The design has no placeholder for it, which is
     * the right behaviour — the product does not ask twice.
     */
    pronouns: z.string().nullable(),
    /**
     * Destination **ids** — the same `destinations` rows the member's own
     * `/me/destinations` draws from, resolved through `GET /destinations` so the
     * chips localise with everything else.
     *
     * ── Same taxonomy, different subject ──────────────────────────────────────
     * A member's own destinations tune the Library and the Coach *for them*; a
     * child's concerns say what *this child* needs. They sit ten lines apart on
     * the profile and mean different things, so the sentence is worth keeping
     * here: nothing about a child's concerns feeds recommendations today, and
     * making them would be a change to ranking rather than a consequence of the
     * shape.
     *
     * **Always present, empty when none** — which is what gives the design's
     * authored "No focus set" something to be the empty state *of*. An absent
     * field would have meant *this route does not answer that*, and it does.
     *
     * Capped at four by the server; on the write an array replaces the set
     * wholesale, so `[]` clears it and omitting it leaves it alone.
     */
    concerns: z.array(z.string().uuid()),
    photoUrl: z.string().nullable(),
    /** Null for a child with no attributed session yet. */
    lastActivity: CrewLastActivity.nullable().optional(),
    /**
     * The before/now pair behind "What changed for each of them".
     *
     * The PARENT writes both. Nothing on this platform records how often a child
     * cried at bedtime — no progress row, challenge entry or kept line says it —
     * so a sentence generated here would be the product telling a parent what
     * changed for their own child, which is the one voice that must not be
     * invented.
     *
     * `nowUpdatedAt` moves only when the `now` line actually CHANGES, so an
     * "as of last month" reading stays true instead of resetting because
     * somebody corrected a name.
     */
    situation: z
        .object({
        before: z.string().nullable(),
        now: z.string().nullable(),
        nowUpdatedAt: z.string().datetime().nullable(),
    })
        .optional(),
    /**
     * Per-child totals, off the `childId` attribution.
     *
     * Zero for a parent who has never attributed a session — a real state rather
     * than an empty one, since the same act lights these and the "FOR LÉO" crumb.
     */
    counts: z
        .object({
        coursesFinished: z.number().int().nonnegative(),
        modulesFinished: z.number().int().nonnegative(),
        anchorsSaved: z.number().int().nonnegative(),
    })
        .optional(),
});
export const CrewSummary = z.object({
    children: z.array(CrewChild),
    coParent: z.string().nullable(),
});
/**
 * Fetch the caller's crew.
 *
 * `enabled` is gated so a caller can render the sidebar for a role that
 * doesn't have a crew widget (Pro/TSP/Org) without firing the request.
 * Cached for a couple of minutes: the sidebar mounts on every route, and
 * a photo URL that is signed anyway can happily come from cache.
 */
export function useCrew(http, options = {}) {
    return useQuery({
        queryKey: ['me', 'crew'],
        enabled: options.enabled ?? true,
        queryFn: () => http.get('/me/crew', { schema: CrewSummary }),
        staleTime: 2 * 60_000,
    });
}
/**
 * Same shape as `CrewChildInput` on the backend.
 *
 * ── `id` is optional to the type and mandatory in practice ───────────────
 * `POST /me/crew` replaces the list wholesale: a row you name by id is updated
 * in place, a row without one is CREATED, and a row you leave out is DELETED.
 *
 * The save used to delete and recreate every child on every submit, which was
 * documented as safe because nothing referenced `children.id`. Progress
 * attribution now does. Dropping the ids therefore does not fail — it silently
 * detaches every session a parent ever attributed, and the crumb and crew
 * sub-lines go blank with nothing in the log. Echo back the ids you were given.
 */
export const CrewChildInput = z.object({
    /** Omit to create. Present = update this row in place. */
    id: z.string().uuid().optional(),
    name: z.string().min(1).max(120),
    birthYear: z.number().int().min(1900).max(2100).optional(),
    photoAssetId: z.string().uuid().optional(),
    /**
     * Free text, cleared with `null`, **left alone when omitted** — the same
     * rule as the before/now pair below, and for the same reason: a drawer
     * saving a name must not wipe a pronoun it never mentioned.
     */
    pronouns: z.string().max(60).nullable().optional(),
    /**
     * Destination ids, at most four.
     *
     * Array-is-authoritative, which is this API's convention wherever a set
     * arrives: any array **replaces** the stored set, so `[]` clears it, and
     * omitting the field leaves it alone. The server refuses an unknown id and
     * a repeated one with `CHILD_CONCERN_INVALID` rather than collapsing the
     * duplicate — a save that quietly stored something other than what it was
     * given is worse than one that says no.
     */
    concerns: z.array(z.string().uuid()).max(4).optional(),
    /**
     * The before/now pair, written by the parent.
     *
     * **An omitted field leaves the pair alone**, so a save that does not mention
     * them cannot wipe what a parent wrote — which is what makes it safe for the
     * crew drawer to keep submitting the whole list.
     */
    situationBefore: z.string().max(600).nullable().optional(),
    situationNow: z.string().max(600).nullable().optional(),
});
/**
 * Replace the caller's crew wholesale.
 *
 * Whole-list, exactly like `useSetNotificationPreference` — the profile
 * screen holds all the state and submits it in one call. The response
 * carries the resolved `CrewSummary` (including newly-signed photo URLs)
 * so it can go straight into the cache the sidebar reads.
 */
export const UpdateCrew = z.object({
    children: z.array(CrewChildInput).max(12),
    coParent: z.string().min(1).max(120).nullable().optional(),
});
export function useUpdateCrew(http) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (input) => http.post('/me/crew', UpdateCrew.parse(input), { schema: CrewSummary }),
        onSuccess: (crew) => queryClient.setQueryData(['me', 'crew'], crew),
    });
}
//# sourceMappingURL=crew.js.map